import { ExternalLink } from "lucide-react";

const spec = (draft, re) => (draft.item_specifics || []).find((s) => s?.value && re.test(s.key || ""))?.value || "";

/** Barcode → catalog number → "Artist - Title" — what Discogs' release search matches best. */
export function discogsSearchTerm(draft) {
  const code = (draft.barcode || spec(draft, /^(upc|ean|barcode)$/i)).replace(/[^0-9]/g, "");
  if (code.length >= 8) return code;
  const cat = spec(draft, /catalog/i);
  const artist = spec(draft, /^(artist|band|performer)$/i);
  const album = spec(draft, /^(release title|album|album name|title)$/i);
  if (cat) return [artist, cat].filter(Boolean).join(" ");
  const term = artist && album ? `${artist} - ${album}` : draft.title || "";
  return term.replace(/\s+/g, " ").trim().slice(0, 120);
}

/** eBay-style condition → Discogs (Goldmine) grade. Seller should still check the copy. */
export function discogsGrade(raw) {
  const c = (raw || "").toLowerCase();
  if (!c) return "";
  if (c.includes("parts") || c.includes("not working")) return "Poor (P)";
  if (c.startsWith("new") || c.includes("sealed")) return "Mint (M)";
  if (c.includes("like new") || c.includes("open box") || c.includes("excellent")) return "Near Mint (NM or M-)";
  if (c.includes("very good")) return "Very Good Plus (VG+)";
  if (c.includes("acceptable")) return "Good Plus (G+)";
  if (c.includes("good")) return "Very Good (VG)";
  return "Very Good Plus (VG+)";
}

function weightGrams(pkg) {
  const oz = (Number(pkg?.weight_lb) || 0) * 16 + (Number(pkg?.weight_oz) || 0);
  return oz > 0 ? String(Math.round(oz * 28.3495)) : "";
}

function comments(draft) {
  const first = (draft.description || "").split(/(?<=[.!?])\s+/).slice(0, 2).join(" ");
  return [draft.condition_notes, first].filter(Boolean).join(" ").replace(/\s+/g, " ").trim().slice(0, 500);
}

export default function DiscogsFields({ draft, channel, Row }) {
  const term = discogsSearchTerm(draft);
  const q = encodeURIComponent(term);
  const grade = discogsGrade(draft.condition);
  const media = draft.record?.media_grade || grade;
  const sleeve = draft.record?.sleeve_grade || grade;
  const graded = !!draft.record?.media_grade;
  return (
    <div className="space-y-2" data-testid="quick-paste-discogs">
      <Row label="Release search" value={term} hint={/^\d{8,}$/.test(term) ? "barcode" : spec(draft, /catalog/i) ? "catalog #" : "artist · title"} testid="discogs-search" channel={channel} />
      <a href={`https://www.discogs.com/search/?q=${q}&type=release`} target="_blank" rel="noopener noreferrer"
        className="flex h-9 items-center justify-center gap-1 rounded-full border border-slate-900 bg-white text-xs font-semibold text-slate-900"
        data-testid="quick-paste-discogs-find">
        <ExternalLink className="h-3 w-3" /> Find the release on Discogs
      </a>
      <p className="px-1 text-[11px] leading-relaxed text-slate-500">
        Open the exact pressing (check the catalog number and matrix on your copy), then tap <b>Sell a copy</b>.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Media condition" value={media} hint={graded ? "AI graded" : "check copy"} testid="discogs-media" channel={channel} />
        <Row label="Sleeve condition" value={sleeve} hint={graded ? "AI graded" : "or Generic"} testid="discogs-sleeve" channel={channel} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Row label="Price (USD)" value={draft.price ? String(draft.price) : ""} testid="price" channel={channel} />
        <Row label="Weight (grams)" value={weightGrams(draft.package)} testid="discogs-weight" channel={channel} />
      </div>
      <Row label="Comments" value={comments(draft)} hint="shown to buyers" testid="discogs-comments" size="lg" channel={channel} />
      <Row label="External ID" value={draft.sku || ""} hint="your SKU" testid="discogs-external-id" channel={channel} />
    </div>
  );
}
